import type { NextPage } from 'next';
import cn from 'classnames';

import { Button, Container } from '@dansk-metal/ui';
import { formatDate } from '@dansk-metal/utils/date';

import DefinitionList from '@web/components/definition-list/definition-list';
import HeroLine from '@web/components/hero-line/hero-line';
import { Aspect } from '@web/components/media/aspectRatios';
import { QBankDocument } from '@web/components/qbank-document/qbank-document';
import { buildUrl } from '@web/components/qbank-image/loader';

import TemplateLayout from '@web/layouts/template/template';

import { UmbracoBlock } from '@web/services/umbraco/types/basic/Block';
import { QBankDocumentType } from '@web/services/umbraco/types/basic/QBank';
import { HTMLString } from '@web/services/umbraco/types/basic/RichTextContent';
import { UmbracoDocument, UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';
import { LinkStyle } from '@web/services/umbraco/types/elements/Action';

import styles from './publication-page.module.scss';

const contentAlias = 'publicationPage' as const;

export type PublicationPageData = UmbracoPage<
	typeof contentAlias,
	{
		title: HTMLString;
		subtitle: string;
		file: QBankDocumentType;
		downloadButton: UmbracoBlock<
			'button',
			{
				title: string;
				style: LinkStyle;
			}
		>;
		issuuURL?: string;
		publisher: string;
		publicationDate: string;
		tags?: UmbracoDocument<'publicationTag', {}>[];
		orderNumber?: string;
	}
>;

const PublicationPageTemplate: NextPage<{ pageData: PublicationPageData }> = ({ pageData }) => {
	const { title, subtitle, file, publisher, publicationDate, tags, orderNumber, issuuURL } =
		pageData?.properties || {};

	const publicationData = [
		{ key: 'Udgiver', value: publisher || '' },
		{ key: 'Udgivelsesdato', value: formatDate(publicationDate, '22. feb 2022') || '' },
		{ key: 'Emne', value: tags?.map((e) => e.name).join(', ') || '' },
		{ key: 'Bestillingsnr.', value: orderNumber || '' },
	];

	return (
		<TemplateLayout>
			<Container className={styles.container}>
				<div className={styles.content}>
					<HeroLine />
					<h1 dangerouslySetInnerHTML={{ __html: title }} />
					<p>{subtitle}</p>
					{issuuURL && (
						<Button href={issuuURL} target="_blank" icon={{ icon: 'book' }}>
							Læs
						</Button>
					)}
					{!issuuURL && file?.filename && (
						<Button href={file.mediaUrl || buildUrl(file.filename)} target="_blank" icon={{ icon: 'download' }}>
							Hent
						</Button>
					)}
					<DefinitionList
						data={publicationData}
						className={cn(styles.definition_list, styles.definition_list__desktop)}
					/>
				</div>
				{file && (
					<div className={styles.cover_image}>
						<QBankDocument media={file} aspectRatio={Aspect['3:4']} />
					</div>
				)}
				<DefinitionList data={publicationData} className={cn(styles.definition_list, styles.definition_list__mobile)} />
			</Container>
		</TemplateLayout>
	);
};

export default PublicationPageTemplate;
